import { css } from "@emotion/react";
import { getVariantAttributeLabel, nonEmpty, type ProductVariantAvailabilityStatus } from "@swo/shared";
import type { EnrichedSwarovskiBundleProductFrontend, TranslatedValue } from "@swo/storefront";
import { nonEmptyArray as NEA } from "fp-ts";
import { pipe } from "fp-ts/lib/function";
import type React from "react";
import { AttributesTag } from "../../../components/_shared-components/ui-elements/AttributesTags/AttributesTag";
import { CSS_MQ } from "../../../styles/breakpoints";
import { FONTS, fontSize } from "../../../styles/fonts";
import { soListStyles } from "../../../styles/rich-text";
import type { BundlePDPVariant, BundleProduct } from "./BundleProduct";
import type { BundleVariantSelection } from "./BundleVariantSelection";
import { getPDPStockLevelIndicator2 } from "../PDP_SingleImageCarousel/price-bar/PDPStockLevelIndicator";
import { StockLevelIndicator } from "../PDP_SingleImageCarousel/price-bar/StockLevelIndicator";
import type { FrontendLineItemAttribute } from "../../../components/HeaderComponent/subcomponents/MenuMetaComponent/ShoppingCart/getLineItemAttributes";

export function toBundleProducts(
  products: EnrichedSwarovskiBundleProductFrontend["products"],
  currentVariantsState: BundleVariantSelection,
  avalabilityMap: Map<string, ProductVariantAvailabilityStatus>,
): Array<BundleProduct> {
  return products.map(product => {
    const selectedVariant: BundlePDPVariant = currentVariantsState.find(v => v.key === product.key)!.value;

    if (!selectedVariant.attributes) {
      throw Error("Cannot use bundle product without attributes");
    }

    const stockLevel = getPDPStockLevelIndicator2(selectedVariant.availability);

    return {
      selectedVariant,
      stockLevel,
      allVariants: pipe(
        product.variants,
        NEA.map(v => {
          const { attributes, ...rest } = v;
          if (!attributes) {
            throw Error("Cannot use bundle product without attributes");
          }

          return {
            ...rest,
            attributes,
            availability: avalabilityMap.get(v.sku) ?? { tag: "out-of-stock" },
            product: {
              name: product.name,
              images: product.images,
              shortDescription: product.shortDescription,
            },
          };
        }),
      ),
      product: { key: product.key, name: product.name },
    };
  });
}

export const BundleContentsList: React.FC<{
  products: Array<BundleProduct>;
  countryIsoCode: string;
  translations: {
    yesLabel: TranslatedValue;
    noLabel: TranslatedValue;
  };
}> = ({ products, countryIsoCode, translations }) => {
  return (
    <ul css={bundleProductsList}>
      {products.map(({ selectedVariant, stockLevel }) => {
        const uniqueAttributesList = selectedVariant.attributes.unique
          .map(u => {
            const label = getVariantAttributeLabel(
              u,
              {
                yesLabel: translations.yesLabel.value,
                noLabel: translations.noLabel.value,
              },
              countryIsoCode,
            );

            return label
              ? {
                  label,
                  type: u.type === "color" ? ("color" as const) : ("plain" as const),
                  customValue: u.type === "color" ? u.value?.hexValue : undefined,
                }
              : undefined;
          })
          .filter(nonEmpty);

        return (
          <li key={selectedVariant.sku} className="bundle-product-list-item">
            <div className="u-flex">
              <span
                css={css`
                  margin-right: 12px;
                `}
              >
                <ProductNameWithAttributes
                  productName={selectedVariant.product.name}
                  attributes={uniqueAttributesList}
                />
              </span>

              {stockLevel.tag !== "in-stock" ? <StockLevelIndicator stockLevel={stockLevel} /> : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export const ProductNameWithAttributes = ({
  productName,
  attributes,
}: {
  productName: string;
  attributes: Array<FrontendLineItemAttribute>;
}) => {
  let label = `${productName}`;

  return (
    <span
      css={css`
        display: inline-flex;
        align-items: center;
        flex-wrap: wrap;
      `}
    >
      <span
        className={attributes.length > 0 ? "with-attributes" : undefined}
        css={css`
          &.with-attributes {
            margin-right: 8px;
          }
        `}
      >
        {label}
      </span>

      {attributes.length > 0 && (
        <ul
          css={css`
            display: flex;
            gap: 8px;
          `}
        >
          {attributes.map(attr => (
            <AttributesTag
              key={attr.label}
              label={attr.label}
              size="small"
              type={attr.type}
              customValue={attr.customValue}
            />
          ))}
        </ul>
      )}
    </span>
  );
};

const bundleProductsList = [
  soListStyles,
  FONTS.SwarovskiFutura300,
  fontSize.x2,
  css`
    margin-left: -6px;
    margin-bottom: 8px;
    margin-top: 20px;

    ${CSS_MQ.atLeastMd} {
      margin-bottom: 12px;
    }

    .bundle-product-list-item + .bundle-product-list-item {
      margin-top: 12px;
    }
  `,
];
